import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatDate, courseImageUrl, type Course, type CourseEdition, type Enrollment } from "@/lib/api";
import { NuevaEdicionModal, EditarCursoModal, EditarEdicionModal } from "@/components/feature/NuevoCursoModal";
import ManualEnrollModal from "@/components/feature/ManualEnrollModal";

export default function CursoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editionModalOpen, setEditionModalOpen] = useState(false);
  const [editCourseOpen, setEditCourseOpen] = useState(false);
  const [editEdition, setEditEdition] = useState<CourseEdition | null>(null);
  const [togglingLanding, setTogglingLanding] = useState(false);
  const [expandedEdition, setExpandedEdition] = useState<string | null>(null);
  const [enrollModalEdition, setEnrollModalEdition] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [courseData, enrollmentsData] = await Promise.all([
        api.getCourse(id),
        api.getEnrollments(),
      ]);
      setCourse(courseData);
      setEnrollments(enrollmentsData.filter((e) => e.course_id === id));
      if (courseData.editions?.length) {
        setExpandedEdition(courseData.editions[0].id);
      }
    } catch {
      setCourse(null);
      setEnrollments([]);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleLanding = async () => {
    if (!course) return;
    setTogglingLanding(true);
    try {
      const updated = await api.updateCourse(course.id, {
        show_on_landing: !course.show_on_landing,
      });
      setCourse(updated);
    } catch {
      /* ignore */
    }
    setTogglingLanding(false);
  };

  const handleDeleteCourse = async () => {
    if (!course) return;
    const editionCount = course.editions?.length || 0;
    
    if (editionCount === 0) {
      if (!confirm("¿Seguro que deseas ELIMINAR COMPLETAMENTE este curso del sistema? Esta acción no se puede deshacer.")) return;
    } else {
      if (!confirm("¿Seguro que deseas archivar este curso? Se ocultará pero mantendrá el historial de sus ediciones.")) return;
    }

    try {
      await api.deleteCourse(course.id);
      navigate("/cursos");
    } catch (err: any) {
      alert(err.message || "Error al eliminar el curso");
    }
  };

  const editions = course?.editions ?? [];

  const getEditionEnrollments = (editionId: string) =>
    enrollments.filter((e) => e.edition_id === editionId);

  const attLabel = (status: string) => {
    switch (status) {
      case "presente":
        return <span className="badge-success">Presente</span>;
      case "ausente":
        return <span className="badge-danger">Ausente</span>;
      default:
        return <span className="badge-info">Pendiente</span>;
    }
  };

  const evalLabel = (status: string) => {
    switch (status) {
      case "aprobado":
        return <span className="badge-success">Aprobado</span>;
      case "rechazado":
        return <span className="badge-danger">Rechazado</span>;
      case "revision":
        return <span className="badge-warning">Revisión</span>;
      default:
        return <span className="badge-info">Pendiente</span>;
    }
  };

  const editionStatusBadge = (status: string) => {
    switch (status) {
      case "programado":
        return <span className="badge-info">Programado</span>;
      case "en_curso":
        return <span className="badge-accent">En Curso</span>;
      case "completado":
        return <span className="badge-success">Completado</span>;
      case "cancelado":
        return <span className="badge-danger">Cancelado</span>;
      default:
        return <span className="badge-info">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
        <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando curso...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-20">
        <i className="ri-book-2-line text-4xl text-[var(--color-text-muted)]"></i>
        <p className="text-[var(--color-text-muted)] mt-3">Curso no encontrado</p>
        <button onClick={() => navigate("/cursos")} className="btn-primary mt-4 cursor-pointer">
          Volver a cursos
        </button>
      </div>
    );
  }

  const attCount = enrollments.filter((e) => e.attendance_status === "presente").length;
  const absCount = enrollments.filter((e) => e.attendance_status === "ausente").length;
  const certCount = enrollments.filter((e) => e.certificate_issued).length;
  const paidCount = enrollments.filter((e) => e.payment_status === "pagado" || e.payment_status === "cortesia").length;
  const evalPending = enrollments.filter(
    (e) => e.attendance_status === "presente" && e.evaluation_status === "pendiente"
  ).length;

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/cursos")}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <div className="flex-1 flex items-start gap-4">
          <img
            src={courseImageUrl(course.image_url)}
            alt={course.name}
            className="w-16 h-16 rounded-xl object-cover border border-[var(--color-border)] flex-shrink-0"
          />
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{course.name}</h1>
              {course.active ? (
                <span className="badge-success">Activo</span>
              ) : (
                <span className="badge-danger">Inactivo</span>
              )}
              <span className="badge-info">{course.category}</span>
            </div>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">{course.description}</p>
          </div>
        </div>
        <button
          onClick={() => setEditCourseOpen(true)}
          className="btn-secondary flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-edit-line"></i>
          Editar
        </button>
        <button
          onClick={handleDeleteCourse}
          className="px-4 py-2 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500/20 transition-colors flex items-center gap-2 cursor-pointer font-medium text-sm"
          title={course?.editions?.length === 0 ? "Eliminar curso completamente" : "Archivar curso"}
        >
          <i className="ri-delete-bin-line"></i>
          Eliminar
        </button>
        <button
          onClick={() => setEditionModalOpen(true)}
          className="btn-primary flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Nueva Edición
        </button>
      </div>

      <div className="card flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={course.show_on_landing}
            disabled={togglingLanding}
            onChange={handleToggleLanding}
            className="cursor-pointer"
          />
          Mostrar en landing page
        </label>
        <span className="text-xs text-[var(--color-text-muted)]">
          {editions.length} edición(es) · {course.duration} · {course.hours}h
        </span>
      </div>

      <div className="card">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Contenido público del detalle</h2>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">Información visible en la página pública del curso.</p>
          </div>
          <button onClick={() => setEditCourseOpen(true)} className="text-xs text-[var(--color-text-accent)] hover:underline cursor-pointer">
            Editar contenido
          </button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Título público</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1 line-clamp-2">{course.detail_title || course.name}</p>
          </div>
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Modalidad</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1">{course.modality || "Por definir"}</p>
          </div>
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Destacados</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1">{course.highlights?.length || 0} punto(s)</p>
          </div>
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Recursos</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1">{course.resource_files?.length || 0} archivo(s)</p>
          </div>
        </div>
        {course.target_audience && (
          <div className="mt-3 bg-[var(--color-bg-tertiary)] rounded-xl p-3 text-sm">
            <span className="text-xs text-[var(--color-text-muted)]">A quién va dirigido</span>
            <p className="text-[var(--color-text-secondary)] mt-1">{course.target_audience}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-[var(--color-text-primary)]">{enrollments.length}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Inscritos</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-[var(--color-text-accent)]">{attCount}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Presentes</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-red-400">{absCount}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Ausentes</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-yellow-400">{evalPending}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Pend. Evaluar</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-green-400">{certCount}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Certificados</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-[var(--color-text-primary)]">
            {Math.round((paidCount / Math.max(enrollments.length, 1)) * 100)}%
          </p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Liquidados</p>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Ediciones del curso</h2>
        {editions.length === 0 ? (
          <div className="card text-center py-12">
            <i className="ri-calendar-line text-3xl text-[var(--color-text-muted)]"></i>
            <p className="text-sm text-[var(--color-text-muted)] mt-2">Sin ediciones registradas</p>
            <button onClick={() => setEditionModalOpen(true)} className="btn-primary mt-4 cursor-pointer">
              Crear primera edición
            </button>
          </div>
        ) : (
          editions.map((edition: CourseEdition) => {
            const editionEnrs = getEditionEnrollments(edition.id);
            const isExpanded = expandedEdition === edition.id;

            return (
              <div key={edition.id} className="card overflow-hidden p-0">
                <button
                  onClick={() => setExpandedEdition(isExpanded ? null : edition.id)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-[var(--color-bg-card-hover)] transition-colors cursor-pointer text-left"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                      {edition.edition_label || `${formatDate(edition.start_date)} — ${formatDate(edition.end_date)}`}
                    </h3>
                    {editionStatusBadge(edition.status)}
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {editionEnrs.length}/{edition.capacity} inscritos
                    </span>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditEdition(edition); }}
                        className="text-xs text-[var(--color-text-accent)] hover:underline cursor-pointer"
                      >
                        Editar
                      </button>
                      {editionEnrs.length === 0 && new Date(edition.start_date).setHours(0,0,0,0) > new Date().setHours(0,0,0,0) && (
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm("¿Estás seguro de eliminar esta edición? Esta acción no se puede deshacer.")) {
                              try {
                                await api.deleteEdition(edition.id);
                                loadData();
                              } catch (err: any) {
                                alert(err.message || "Error al eliminar");
                              }
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-300 hover:underline cursor-pointer"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                  <i className={`ri-${isExpanded ? "arrow-up-s" : "arrow-down-s"}-line text-[var(--color-text-muted)]`}></i>
                </button>

                {isExpanded && (
                  <div className="border-t border-[var(--color-border)]">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-4 text-sm">
                      <div>
                        <span className="text-xs text-[var(--color-text-muted)]">Fechas</span>
                        <p className="text-[var(--color-text-primary)] font-medium">
                          {formatDate(edition.start_date)} → {formatDate(edition.end_date)}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-[var(--color-text-muted)]">Ubicación</span>
                        <p className="text-[var(--color-text-primary)] font-medium">{edition.location}</p>
                      </div>
                      <div>
                        <span className="text-xs text-[var(--color-text-muted)]">Precio</span>
                        <p className="text-[var(--color-text-accent)] font-semibold">
                          ${edition.price.toLocaleString("en-US")} USD
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-[var(--color-text-muted)]">Personal</span>
                        <p className="text-[var(--color-text-primary)] font-medium text-xs">
                          {edition.instructor?.full_name || "Sin instructor"} · {edition.evaluator?.full_name || "Sin evaluador"}
                        </p>
                      </div>
                    </div>

                    <div className="px-6 pb-3 flex justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEnrollModalEdition(edition.id); }}
                        className="btn-secondary text-xs flex items-center gap-2 cursor-pointer"
                      >
                        <i className="ri-user-add-line"></i>
                        Inscribir Estudiante
                      </button>
                    </div>

                    {editionEnrs.length === 0 ? (
                      <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
                        Sin estudiantes inscritos en esta edición
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[var(--color-border)]">
                              <th className="table-header">Estudiante</th>
                              <th className="table-header">Empresa</th>
                              <th className="table-header">Asistencia</th>
                              <th className="table-header">Evaluación</th>
                              <th className="table-header">Pago</th>
                              <th className="table-header">Certificado</th>
                              <th className="table-header">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {editionEnrs.map((enr) => (
                              <tr
                                key={enr.id}
                                className="hover:bg-[var(--color-bg-card-hover)] transition-colors cursor-pointer"
                                onClick={() => navigate(`/estudiantes/${enr.student_id}`)}
                              >
                                <td className="table-cell">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 rounded-lg bg-[var(--color-accent)]/15 flex items-center justify-center flex-shrink-0">
                                      <span className="text-[var(--color-text-accent)] text-[10px] font-semibold">
                                        {(enr.student?.full_name || "?").charAt(0)}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-[var(--color-text-primary)]">
                                        {enr.student?.full_name || "Desconocido"}
                                      </p>
                                      <p className="text-xs text-[var(--color-text-muted)]">
                                        {enr.student?.email}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td className="table-cell">
                                  <span className="text-sm text-[var(--color-text-secondary)]">
                                    {enr.student?.company || "—"}
                                  </span>
                                </td>
                                <td className="table-cell">{attLabel(enr.attendance_status)}</td>
                                <td className="table-cell">{evalLabel(enr.evaluation_status)}</td>
                                <td className="table-cell">
                                  {enr.payment_status === "pagado" ? (
                                    <span className="badge-success">Pagado</span>
                                  ) : enr.payment_status === "cortesia" ? (
                                    <span className="badge-info">Cortesía</span>
                                  ) : enr.payment_status === "abono_parcial" ? (
                                    <span className="badge-warning">Parcial</span>
                                  ) : (
                                    <span className="badge-danger">Pendiente</span>
                                  )}
                                </td>
                                <td className="table-cell">
                                  {enr.certificate_issued ? (
                                    <span className="badge-success">Emitido</span>
                                  ) : (
                                    <span className="text-xs text-[var(--color-text-muted)]">—</span>
                                  )}
                                </td>
                                <td className="table-cell">
                                  {enr.payment_status !== "cortesia" && (
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-[var(--color-accent-oil)]/40 text-[var(--color-accent-oil)] bg-[var(--color-accent-oil)]/10 hover:bg-[var(--color-accent-oil)]/20 transition-colors cursor-pointer"
                                      title="Exonerar pago por cortesía (no cuenta como ingreso)"
                                      onClick={async () => {
                                        const alreadyPaid = enr.payment_status === "pagado" || enr.payment_status === "abono_parcial";
                                        if (!confirm(
                                          alreadyPaid
                                            ? `¿Convertir a cortesía/exonerado a ${enr.student?.full_name || "este estudiante"}?\n\nQuedará liquidado, los pagos reales se anularán para que NO sumen a las ganancias.`
                                            : `¿Marcar cortesía/exonerado para ${enr.student?.full_name || "este estudiante"}?\n\nQuedará liquidado para certificados, pero NO sumará a las ganancias.`
                                        )) return;
                                        try {
                                          await api.markEnrollmentCourtesy(enr.id);
                                          loadData();
                                        } catch (err: any) {
                                          alert(err.message || "No se pudo marcar cortesía");
                                        }
                                      }}
                                    >
                                      <i className="ri-gift-2-line text-sm"></i>
                                      Cortesía
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <NuevaEdicionModal
        isOpen={editionModalOpen}
        onClose={() => setEditionModalOpen(false)}
        courseId={course.id}
        onCreated={loadData}
      />
      <EditarCursoModal
        isOpen={editCourseOpen}
        onClose={() => setEditCourseOpen(false)}
        course={course}
        onSaved={loadData}
      />
      {editEdition && (
        <EditarEdicionModal
          isOpen
          onClose={() => setEditEdition(null)}
          edition={editEdition}
          onSaved={loadData}
        />
      )}
      {enrollModalEdition && (
        <ManualEnrollModal
          isOpen
          onClose={() => setEnrollModalEdition(null)}
          editionId={enrollModalEdition}
          onSuccess={loadData}
        />
      )}
    </div>
  );
}
